import React, { useRef, useState, isValidElement, useCallback, createRef } from 'react';
import { VerticalSnapperSteps } from './VerticalSnapperSteps';
import { VerticalSnapperFrame } from './VerticalSnapperFrame';
import './VerticalSnapper.scss';

export type VerticalSnapperProps = React.HTMLAttributes<HTMLDivElement> & {
  onEnter?(index: number): void;
  onLeave?(index: number): void;
};

export const VerticalSnapper: React.FC<VerticalSnapperProps> = ({
  children,
  onEnter,
  onLeave,
  ...rest
}) => {
  const frames = React.Children.toArray(children)
    .filter(isValidElement)
    .map(child => ({ child, ref: createRef<HTMLSpanElement>() }));

  const [index, setIndex] = useState(0);

  const railRef = useRef<HTMLDivElement | null>(null);

  const handleEnter = useCallback(
    (index: number) => {
      setIndex(index);
      onEnter?.(index);
    },
    [onEnter]
  );

  const handleLeave = useCallback(
    (index: number) => {
      onLeave?.(index);
    },
    [onLeave]
  );

  const handleClick = useCallback(
    (step: number) => {
      if (!railRef.current) return;
      railRef.current.scrollTop = frames[step].ref.current?.offsetTop || 0;
    },
    [frames]
  );

  return (
    <div className="VerticalSnapper" {...rest}>
      <VerticalSnapperSteps length={frames.length} index={index} onClick={handleClick} />

      <div className="VerticalSnapper__rail" ref={railRef}>
        {frames.map((frame, index) => (
          <VerticalSnapperFrame
            key={index}
            index={index}
            onEnter={handleEnter}
            onLeave={handleLeave}
          >
            <span ref={frame.ref} />

            {frame.child}
          </VerticalSnapperFrame>
        ))}
      </div>
    </div>
  );
};
